/**
 * 存档管理器
 * 处理游戏进度的保存和加载
 */
import { GameSave, GameConfig, PieceState } from '../types';

export interface SaveSlot {
    id: string;
    name: string;
    timestamp: number;
    thumbnail?: string;
    pieceCount: number;
    placedCount: number;
    elapsedTime: number;
}

export class SaveManager {
    private static instance: SaveManager;
    private readonly STORAGE_KEY = 'jigsaw_hex_saves';
    private readonly CURRENT_VERSION = '1.0.0';
    private readonly MAX_SLOTS = 10;

    private constructor() {}

    /**
     * 获取单例实例
     */
    static getInstance(): SaveManager {
        if (!SaveManager.instance) {
            SaveManager.instance = new SaveManager();
        }
        return SaveManager.instance;
    }

    /**
     * 保存游戏
     */
    save(
        slotId: string,
        config: GameConfig,
        pieces: PieceState[],
        elapsedTime: number,
        moveCount: number,
        imageData?: string,
        puzzleId?: string,
        puzzleName?: string
    ): boolean {
        try {
            const saves = this.getAllSaves();

            const save: GameSave = {
                version: this.CURRENT_VERSION,
                timestamp: Date.now(),
                puzzleId,
                puzzleName,
                config,
                pieces,
                elapsedTime,
                moveCount,
                imageData
            };

            saves[slotId] = save;

            // 限制存档数量
            const slotIds = Object.keys(saves);
            if (slotIds.length > this.MAX_SLOTS) {
                // 删除最旧的存档
                const oldest = slotIds.reduce((a, b) =>
                    saves[a].timestamp < saves[b].timestamp ? a : b
                );
                delete saves[oldest];
            }

            localStorage.setItem(this.STORAGE_KEY, JSON.stringify(saves));
            return true;
        } catch (error) {
            console.error('Failed to save game:', error);
            return false;
        }
    }

    /**
     * 加载游戏
     */
    load(slotId: string): GameSave | null {
        try {
            const saves = this.getAllSaves();
            const save = saves[slotId];

            if (!save) return null;

            // 版本兼容性检查
            if (save.version !== this.CURRENT_VERSION) {
                console.warn(`Save version mismatch: ${save.version} vs ${this.CURRENT_VERSION}`);
            }

            return save;
        } catch (error) {
            console.error('Failed to load game:', error);
            return null;
        }
    }

    /**
     * 删除存档
     */
    delete(slotId: string): boolean {
        try {
            const saves = this.getAllSaves();

            if (!saves[slotId]) return false;

            delete saves[slotId];
            localStorage.setItem(this.STORAGE_KEY, JSON.stringify(saves));
            return true;
        } catch (error) {
            console.error('Failed to delete save:', error);
            return false;
        }
    }

    /**
     * 获取所有存档
     */
    private getAllSaves(): Record<string, GameSave> {
        try {
            const data = localStorage.getItem(this.STORAGE_KEY);
            return data ? JSON.parse(data) : {};
        } catch {
            return {};
        }
    }

    /**
     * 获取存档列表
     */
    getSaveSlots(): SaveSlot[] {
        const saves = this.getAllSaves();

        return Object.entries(saves).map(([id, save]) => ({
            id,
            name: `存档 ${id}`,
            timestamp: save.timestamp,
            pieceCount: save.pieces.length,
            placedCount: save.pieces.filter(p => p.isPlaced).length,
            elapsedTime: save.elapsedTime
        })).sort((a, b) => b.timestamp - a.timestamp);
    }

    /**
     * 获取最新的一条存档 (无论是自动还是手动)
     */
    getLatestSave(): GameSave | null {
        const saves = this.getAllSaves();
        const slotIds = Object.keys(saves);
        if (slotIds.length === 0) return null;

        const latestId = slotIds.reduce((a, b) =>
            saves[a].timestamp > saves[b].timestamp ? a : b
        );
        return saves[latestId];
    }

    /**
     * 检查存档是否存在
     */
    exists(slotId: string): boolean {
        const saves = this.getAllSaves();
        return !!saves[slotId];
    }

    /**
     * 自动保存
     */
    autoSave(
        config: GameConfig,
        pieces: PieceState[],
        elapsedTime: number,
        moveCount: number,
        imageData?: string,
        puzzleId?: string,
        puzzleName?: string
    ): boolean {
        return this.save('auto', config, pieces, elapsedTime, moveCount, imageData, puzzleId, puzzleName);
    }

    /**
     * 加载自动存档
     */
    loadAutoSave(): GameSave | null {
        return this.load('auto');
    }

    /**
     * 检查是否有自动存档
     */
    hasAutoSave(): boolean {
        return this.exists('auto');
    }

    /**
     * 清空所有存档
     */
    clearAll(): void {
        localStorage.removeItem(this.STORAGE_KEY);
    }

    /**
     * 导出存档为 JSON 文件
     */
    exportSave(slotId: string): string | null {
        const save = this.load(slotId);
        if (!save) return null;

        return JSON.stringify(save, null, 2);
    }

    /**
     * 从 JSON 导入存档
     */
    importSave(slotId: string, jsonData: string): boolean {
        try {
            const save = JSON.parse(jsonData) as GameSave;

            // 验证数据结构
            if (!save.version || !save.config || !save.pieces) {
                throw new Error('Invalid save data structure');
            }

            const saves = this.getAllSaves();
            saves[slotId] = save;
            localStorage.setItem(this.STORAGE_KEY, JSON.stringify(saves));

            return true;
        } catch (error) {
            console.error('Failed to import save:', error);
            return false;
        }
    }

    /**
     * 格式化时间显示
     */
    static formatTime(milliseconds: number): string {
        const seconds = Math.floor(milliseconds / 1000);
        const minutes = Math.floor(seconds / 60);
        const hours = Math.floor(minutes / 60);

        if (hours > 0) {
            return `${hours}:${String(minutes % 60).padStart(2, '0')}:${String(seconds % 60).padStart(2, '0')}`;
        }

        return `${minutes}:${String(seconds % 60).padStart(2, '0')}`;
    }

    /**
     * 格式化日期显示
     */
    static formatDate(timestamp: number): string {
        const date = new Date(timestamp);
        return date.toLocaleString('zh-CN', {
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
            hour: '2-digit',
            minute: '2-digit'
        });
    }

    /**
     * 获取存储使用情况
     */
    getStorageUsage(): { used: number; total: number } {
        let used = 0;

        try {
            const data = localStorage.getItem(this.STORAGE_KEY);
            if (data) {
                used = new Blob([data]).size;
            }
        } catch {}

        // localStorage 通常限制为 5MB
        const total = 5 * 1024 * 1024;

        return { used, total };
    }
}
